import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import { Apii } from '@/services/api.js';

export function useProductChanges() {
    const ApiService = new Apii();
    const walletStore = useWalletStore();
    const { showProductChangeModal } = storeToRefs(walletStore);
    const { showNotification, setShowProductChangeModal } = walletStore;

    const productChanges = ref([
        {
            previous_card_id: null,
            product_changed_date_opened: null,
            product_changed_date: null,
        },
    ]);

    const editingProductChange = ref(null);
    const editingProductChangeIndex = ref(null);
    const editingChangedToCard = ref(null);
    const editingIsOriginalProductChange = ref(false);
    const selectedProductChangeOption = ref('');

    // Snapshot of the card before a change-to-new product change is applied in
    // this modal session, so deleting the change can restore it without a reload.
    const originalCardState = ref(null);

    const openProductChangeModal = () => {
        editingProductChange.value = null;
        editingProductChangeIndex.value = null;
        editingChangedToCard.value = null;
        editingIsOriginalProductChange.value = false;
        selectedProductChangeOption.value = '';
        setShowProductChangeModal(true);
    };

    const handleProductChangeModalClose = () => {
        editingProductChange.value = null;
        editingProductChangeIndex.value = null;
        editingChangedToCard.value = null;
        editingIsOriginalProductChange.value = false;
        setShowProductChangeModal(false);
    };

    const getSortedProductChanges = () => {
        if (!productChanges.value || productChanges.value.length === 0)
            return [];
        return [...productChanges.value].sort((a, b) => {
            const dateA = a.product_changed_date
                ? new Date(a.product_changed_date)
                : null;
            const dateB = b.product_changed_date
                ? new Date(b.product_changed_date)
                : null;
            const timeA = dateA ? dateA.getTime() : null;
            const timeB = dateB ? dateB.getTime() : null;
            const validA = timeA !== null && !Number.isNaN(timeA);
            const validB = timeB !== null && !Number.isNaN(timeB);
            if (!validA && !validB) return 0;
            if (!validA) return 1;
            if (!validB) return -1;
            return timeB - timeA;
        });
    };

    const editProductChange = (productChange, index, localCard, card) => {
        const sortedChanges = getSortedProductChanges();

        let resolvedIndex = index;
        if (resolvedIndex === undefined || resolvedIndex === null) {
            resolvedIndex = sortedChanges.findIndex(
                (pc) => pc.id === productChange.id
            );
            if (resolvedIndex === -1) resolvedIndex = 0;
        }

        editingProductChange.value = productChange;
        editingProductChangeIndex.value = resolvedIndex;
        selectedProductChangeOption.value = 'add-previous';

        if (resolvedIndex === 0) {
            editingChangedToCard.value =
                localCard.card || card?.card || null;
        } else {
            editingChangedToCard.value =
                sortedChanges[resolvedIndex - 1]?.previous_card || null;
        }

        editingIsOriginalProductChange.value =
            resolvedIndex === sortedChanges.length - 1;

        setShowProductChangeModal(true);
    };

    const saveProductChange = async (
        cardData,
        localCard,
        selectedCard,
        bonusOffers,
        originalBonusOffers,
        resetBonusOffers
    ) => {
        // CardSearch emits null when the user clears their selection with the
        // clear (X) button (SUPPORT-378). Clearing is undoing a pending product
        // change, not making one, so drop the original-card references and bail.
        if (!cardData) {
            localCard.value.original_card = null;
            localCard.value.original_card_id = null;
            return;
        }

        try {
            const productChangePayload = {
                previous_card_id: null,
                new_card_id: null,
                product_changed_date_opened:
                    cardData.product_changed_date_opened || null,
                product_changed_date: cardData.product_changed_date || null,
            };

            if (cardData.selectedOption === 'add-previous') {
                productChangePayload.previous_card_id = cardData.id || null;
                productChangePayload.new_card_id = localCard.value.card_id;
            } else if (cardData.selectedOption === 'change-to-new') {
                productChangePayload.previous_card_id =
                    localCard.value.card_id;
                productChangePayload.new_card_id = cardData.id || null;

                if (
                    !productChangePayload.product_changed_date_opened &&
                    localCard.value.date_opened
                ) {
                    productChangePayload.product_changed_date_opened =
                        localCard.value.date_opened;
                }
            }

            if (cardData.isEditing && cardData.editingProductChangeId) {
                await ApiService.apiCall(
                    `/wallets/cards/${localCard.value.id}/product-changes/${cardData.editingProductChangeId}`,
                    'put',
                    productChangePayload
                );

                const index = productChanges.value.findIndex(
                    (pc) => pc.id === cardData.editingProductChangeId
                );
                if (index !== -1) {
                    productChanges.value[index] = {
                        ...productChanges.value[index],
                        previous_card_id:
                            productChangePayload.previous_card_id,
                        previous_card: cardData,
                        product_changed_date_opened:
                            productChangePayload.product_changed_date_opened,
                        product_changed_date:
                            productChangePayload.product_changed_date,
                    };
                }

                handleProductChangeModalClose();

                showNotification(
                    'Product Change Updated',
                    'Your product change has been successfully updated.'
                );
            } else {
                const response = await ApiService.apiCall(
                    `/wallets/cards/${localCard.value.id}/product-changes`,
                    'post',
                    productChangePayload
                );

                productChanges.value = productChanges.value.filter(
                    (pc) => pc.previous_card_id !== null
                );

                if (response?.data?.product_change) {
                    productChanges.value.push(response.data.product_change);
                } else {
                    productChanges.value.push({
                        id: response?.data?.id || null,
                        previous_card_id:
                            productChangePayload.previous_card_id,
                        previous_card: cardData.previous_card || cardData,
                        product_changed_date_opened:
                            productChangePayload.product_changed_date_opened,
                        product_changed_date:
                            productChangePayload.product_changed_date,
                    });
                }

                if (cardData.selectedOption === 'change-to-new') {
                    // Snapshot the pre-change card so deleting this product
                    // change later in the session can restore it.
                    originalCardState.value = {
                        card_id: localCard.value.card_id,
                        name: localCard.value.name,
                        card: localCard.value.card,
                    };

                    localCard.value.card_id = cardData.id || null;
                    localCard.value.name = cardData.name || '';
                    localCard.value.card = cardData;

                    if (bonusOffers.value.some((offer) => offer.type)) {
                        originalBonusOffers.value = bonusOffers.value.map(
                            (offer) => ({ ...offer, product_changed: true })
                        );
                        resetBonusOffers();
                    }
                }

                localCard.value.product_changed = true;
                localCard.value.product_changed_date =
                    productChangePayload.product_changed_date;
                localCard.value.product_changed_date_opened =
                    productChangePayload.product_changed_date_opened;

                localCard.value.product_changes = [...productChanges.value];

                if (selectedCard.value) {
                    selectedCard.value.product_changed = true;
                    selectedCard.value.product_changed_date =
                        productChangePayload.product_changed_date;
                    selectedCard.value.product_changed_date_opened =
                        productChangePayload.product_changed_date_opened;
                    selectedCard.value.product_changes = [
                        ...productChanges.value,
                    ];
                }

                handleProductChangeModalClose();

                showNotification(
                    'Product Change Added',
                    'Your product change has been successfully added.'
                );
            }
        } catch (error) {
            console.error('Error saving product change:', error);
            showNotification(
                'Error',
                'Failed to save product change. Please try again.',
                10000,
                'error'
            );
        }
    };

    // Sync the modal's card fields from a connected_account API payload.
    // The backend restores card_id when a product change is deleted, so the
    // modal has to adopt the fresh values or a later Save would write the
    // stale card back.
    const applyRestoredCard = (localCard, connectedAccount) => {
        if (!localCard?.value || !connectedAccount) {
            return;
        }

        localCard.value.card_id = connectedAccount.card_id;
        localCard.value.original_card_id = connectedAccount.original_card_id;
        localCard.value.original_card = connectedAccount.original_card || null;
        if (connectedAccount.card) {
            localCard.value.card = connectedAccount.card;
            localCard.value.name = connectedAccount.card.title || '';
        }
    };

    const resetProductChanges = (localCard = null, updatedConnectedAccount = null) => {
        if (localCard?.value) {
            if (updatedConnectedAccount) {
                // The backend response is the most reliable restore source.
                applyRestoredCard(localCard, updatedConnectedAccount);
                localCard.value.product_changed =
                    updatedConnectedAccount.product_changed;
                localCard.value.product_changed_date =
                    updatedConnectedAccount.product_changed_date;
                localCard.value.product_changed_date_opened =
                    updatedConnectedAccount.product_changed_date_opened;
            } else if (originalCardState.value) {
                // Fallback: in-memory snapshot from the current modal session.
                localCard.value.card_id = originalCardState.value.card_id;
                localCard.value.name = originalCardState.value.name;
                localCard.value.card = originalCardState.value.card;
                localCard.value.product_changed = false;
                localCard.value.product_changed_date = null;
                localCard.value.product_changed_date_opened = null;
                localCard.value.original_card = null;
                localCard.value.original_card_id = null;
            } else if (
                localCard.value.original_card_id &&
                localCard.value.original_card
            ) {
                // Fallback: original card tracked on the connected account.
                localCard.value.card_id = localCard.value.original_card_id;
                localCard.value.name =
                    localCard.value.original_card.title || '';
                localCard.value.card = localCard.value.original_card;
                localCard.value.product_changed = false;
                localCard.value.product_changed_date = null;
                localCard.value.product_changed_date_opened = null;
                localCard.value.original_card = null;
                localCard.value.original_card_id = null;
            } else {
                localCard.value.product_changed = false;
            }
        }

        originalCardState.value = null;

        productChanges.value = [
            {
                previous_card_id: null,
                product_changed_date: null,
                product_changed_date_opened: null,
            },
        ];
    };

    return {
        productChanges,
        editingProductChange,
        editingProductChangeIndex,
        editingChangedToCard,
        editingIsOriginalProductChange,
        selectedProductChangeOption,
        showProductChangeModal,
        openProductChangeModal,
        handleProductChangeModalClose,
        editProductChange,
        getSortedProductChanges,
        saveProductChange,
        applyRestoredCard,
        resetProductChanges,
    };
}
